/* global VERSION, BUILDTIME */
// $FlowFixMe
import './assets/style-overrides.scss'
import './util/polyfills'
import { getMaskAsUserId, setMaskAsUserId } from './util/maskAsUser'
import { initConfig } from './util/config'
import { loginComplete as loginCompletAction } from './ducks/user/creators'
import { observeStore } from './util/redux'
import { persistStore } from 'redux-persist'
import { render } from 'react-dom'
import { userIdSelector } from './ducks/user/selectors'
import * as React from 'react'
import createFilter from 'redux-persist-transform-filter'
import 'regenerator-runtime/runtime.js'

/**
 * Starting point for app; acts as entry for `webpack`; anything setup related or auth-wide should be done here
 * @summary Starting point for app
 * @module index
 */

// If this is an admin user returning from God Mode, clear old user ID and refresh page
if (getMaskAsUserId()) {
  setMaskAsUserId()
  window.location.replace('/')
}

// Init config data
initConfig().then(() => {
  Promise.all([
    import(/* webpackChunkName: "routes" */ /* Router from */ './routes'),
    import(
      /* webpackChunkName: "event-switchboard" */ /*1*/ /* eventSwitchboard from */ './ducks/eventSwitchboard'
    ),
    import(
      /* webpackChunkName: "store" */ /*2*/ /* { Store } from */ './store/store'
    ),
    import(
      /* webpackChunkName: "util-lg" */ /*3*/ /* { logger } from */ './util/logger'
    ),
    import(
      /* webpackChunkName: "user-methods" */ /*4*/ /* { subscribeToUpdates } from */ './ducks/user/methods'
    ),
    import(
      /* webpackChunkName: "service-worker" */ /*5*/ /* { registerServiceWorker } from */ './util/serviceWorker'
    ),
    import(
      /* webpackChunkName: "util-app" */ /*6*/ /* { isDev } from */ './util/app'
    ),
    import(/* webpackChunkName: "util-help" */ /*7*/ './util/help'),
    import(/* webpackChunkName: "util-wc" */ /*8*/ './util/tracker')
  ]).then(modules => {
    const Router = modules[0].default
    const eventSwitchboard = modules[1].default
    const Store = modules[2].Store
    const initLogging = modules[3].initLogging
    const subscribeToUpdates = modules[4].subscribeToUpdates
    const registerServiceWorker = modules[5].registerServiceWorker
    const isDev = modules[6].isDev
    const createEventListener = modules[6].createEventListener
    const isLimitedAccess = modules[6].isLimitedAccess
    const watchForLogout = modules[6].watchForLogout
    const help = modules[7]
    const initTracker = modules[8].init

    // Logging ////////////////////////////////////////////////////////////////

    // Init logging
    const logger = initLogging(VERSION, Store)

    // Init tracking/analytics
    initTracker()

    // Log version, build time
    logger.info(
      `VERSION: ${VERSION} | BUILT: ${new Date(BUILDTIME).toString()}`
    )

    // Log if limmited access
    if (isLimitedAccess()) {
      logger.info('User is Limited Access')
    }

    // Provide root element if none (useful for testing)
    if (document.getElementById('root') === null) {
      document.body.innerHTML += '<main id="root"></main>'
    }

    // Capture initially requested route
    const initialPath = window.location.pathname + window.location.search

    // Setup Service Worker ///////////////////////////////////////////////////

    if (!isDev()) {
      registerServiceWorker()
    }

    // Store //////////////////////////////////////////////////////////////////

    // Rehydrate stored user creds and kick off routing and help
    persistStore(
      Store,
      {
        blacklist: [
          'routing',
          'router',
          'location',
          'schedule',
          'zone',
          'device'
        ],
        transforms: [createFilter('user', ['META'])]
      },
      (err, oldStore) => {
        // Setup help and init Routing
        // Note that help should be setup before `render` is called so that the
        // indivdual containers/components can set their help screen labels
        help.setup(Store)

        // The persistStore lib correctly stores the state, but does not
        // correctly re-hydrate it; so we do that manually here
        if (
          oldStore &&
          oldStore.user &&
          oldStore.user.META &&
          oldStore.user.META.currentUser &&
          oldStore.user.META.currentUser.access_token
        ) {
          Store.dispatch(loginCompletAction(oldStore.user.META.currentUser))
        }

        // Render App!
        render(<Router Store={Store} />, document.getElementById('root'))

        // Watch for user logout
        watchForLogout(Store)
      }
    )

    // Events/MQTT ////////////////////////////////////////////////////////////
    if (!isLimitedAccess(initialPath)) {
      // Limited access handles this on it's own
      createEventListener(
        userIdSelector(Store.getState()),
        Store,
        eventSwitchboard,
        subscribeToUpdates,
        err => logger.error(new Error('EVENT_LISTENER_ERROR'), { error: err })
      ).then(eventListener =>
        observeStore(Store, userIdSelector, eventListener)
      )
    }
  })
})

// Allow hot-reloading
if (module.hot && typeof module.hot.accept === 'function') {
  // Accept hot reloading
  module.hot.accept()
}
